import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, UtensilsCrossed, Calendar, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/Card";
import { cn } from "@/lib/cn";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface MealRecord {
  id: number;
  agencyId: number;
  date: string;
  mealType: string;
  count: number;
  siteId: number | null;
  createdBy: number | null;
}

interface MealListResponse {
  data: MealRecord[];
  meta: { total: number };
}

interface MealSummaryResponse {
  data: {
    byDate: { date: string; mealType: string; totalCount: number }[];
    totals: { mealType: string; totalCount: number }[];
    grandTotal: number;
  };
}

type ViewMode = "daily" | "summary";

const MEAL_TYPES = ["breakfast", "lunch", "dinner", "snack"] as const;

const MEAL_LABELS: Record<string, string> = {
  breakfast: "Breakfast",
  lunch: "Lunch",
  dinner: "Dinner",
  snack: "Snack",
};

const MEAL_COLORS: Record<string, string> = {
  breakfast: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
  lunch: "bg-green-100 text-green-800 dark:bg-green-950 dark:text-green-300",
  dinner: "bg-blue-100 text-blue-800 dark:bg-blue-950 dark:text-blue-300",
  snack: "bg-purple-100 text-purple-800 dark:bg-purple-950 dark:text-purple-300",
};

// ---------------------------------------------------------------------------
// Quick Entry Form
// ---------------------------------------------------------------------------

function QuickEntryForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const today = new Date().toISOString().split("T")[0];
  const [date, setDate] = useState(today);
  const [mealType, setMealType] = useState<string>("breakfast");
  const [count, setCount] = useState("");

  const recordMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post("/shelter/meals", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["meals"] });
      queryClient.invalidateQueries({ queryKey: ["mealSummary"] });
      onClose();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsedCount = parseInt(count, 10);
    if (isNaN(parsedCount) || parsedCount < 0) return;

    recordMutation.mutate({
      date,
      mealType,
      count: parsedCount,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Record Meal Count</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Input
              label="Date"
              type="date"
              required
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />

            <div className="space-y-1.5">
              <label className="text-sm font-medium leading-none text-foreground">
                Meal Type
              </label>
              <select
                value={mealType}
                onChange={(e) => setMealType(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {MEAL_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {MEAL_LABELS[type]}
                  </option>
                ))}
              </select>
            </div>

            <Input
              label="Count"
              type="number"
              required
              min={0}
              value={count}
              onChange={(e) => setCount(e.target.value)}
              placeholder="0"
            />
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={recordMutation.isPending}>
              <Plus className="h-4 w-4" />
              Record
            </Button>
          </div>

          {recordMutation.isError && (
            <p className="text-sm text-destructive">
              Failed to record meal. Please try again.
            </p>
          )}
        </form>
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// MealTracking Component
// ---------------------------------------------------------------------------

export function MealTracking() {
  const today = new Date().toISOString().split("T")[0] as string;
  const [viewMode, setViewMode] = useState<ViewMode>("daily");
  const [selectedDate, setSelectedDate] = useState(today);
  const [showAddForm, setShowAddForm] = useState(false);

  // Calculate week range for summary view
  const weekRange = useMemo(() => {
    const d = new Date(selectedDate);
    const dayOfWeek = d.getDay();
    const startOfWeek = new Date(d);
    startOfWeek.setDate(d.getDate() - dayOfWeek);
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6);

    return {
      start: startOfWeek.toISOString().split("T")[0] as string,
      end: endOfWeek.toISOString().split("T")[0] as string,
    };
  }, [selectedDate]);

  // Daily view query
  const { data: dailyData, isLoading: dailyLoading } =
    useQuery<MealListResponse>({
      queryKey: ["meals", "daily", selectedDate],
      queryFn: () =>
        api.get<MealListResponse>("/shelter/meals", {
          params: { date: selectedDate },
        }),
      enabled: viewMode === "daily",
    });

  // Summary view query
  const { data: summaryData, isLoading: summaryLoading } =
    useQuery<MealSummaryResponse>({
      queryKey: ["mealSummary", weekRange.start, weekRange.end],
      queryFn: () =>
        api.get<MealSummaryResponse>("/shelter/meals/summary", {
          params: {
            startDate: weekRange.start,
            endDate: weekRange.end,
          },
        }),
      enabled: viewMode === "summary",
    });

  const isLoading = viewMode === "daily" ? dailyLoading : summaryLoading;

  // Group daily meals by type for display
  const dailyMealsByType = useMemo(() => {
    const meals = dailyData?.data || [];
    const byType: Record<string, number> = {};
    for (const meal of meals) {
      byType[meal.mealType] = (byType[meal.mealType] || 0) + meal.count;
    }
    return byType;
  }, [dailyData]);

  const dailyTotal = Object.values(dailyMealsByType).reduce(
    (sum, count) => sum + count,
    0,
  );

  // Build weekly grid from summary data
  const weeklyGrid = useMemo(() => {
    if (!summaryData?.data?.byDate) return [];

    const dateMap = new Map<string, Record<string, number>>();

    for (const entry of summaryData.data.byDate) {
      if (!dateMap.has(entry.date)) {
        dateMap.set(entry.date, {});
      }
      dateMap.get(entry.date)![entry.mealType] = entry.totalCount;
    }

    // Generate all 7 days of the week
    const days: { date: string; dayLabel: string; meals: Record<string, number> }[] = [];
    const start = new Date(weekRange.start + "T00:00:00");
    const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    for (let i = 0; i < 7; i++) {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      const dateStr = d.toISOString().split("T")[0] as string;
      days.push({
        date: dateStr,
        dayLabel: `${dayNames[d.getDay()]} ${d.getMonth() + 1}/${d.getDate()}`,
        meals: dateMap.get(dateStr) || {},
      });
    }

    return days;
  }, [summaryData, weekRange]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Meal Tracking</h1>
          <p className="text-sm text-muted-foreground">
            Track daily meal counts for shelter residents
          </p>
        </div>
        <Button onClick={() => setShowAddForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          Record Meal
        </Button>
      </div>

      {/* Add Form */}
      {showAddForm && <QuickEntryForm onClose={() => setShowAddForm(false)} />}

      {/* Controls */}
      <div className="flex flex-wrap items-center gap-4">
        {/* View mode toggle */}
        <div className="flex gap-1 rounded-lg bg-muted p-1">
          <button
            onClick={() => setViewMode("daily")}
            className={cn(
              "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              viewMode === "daily"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <Calendar className="h-3.5 w-3.5" />
            Daily
          </button>
          <button
            onClick={() => setViewMode("summary")}
            className={cn(
              "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
              viewMode === "summary"
                ? "bg-background text-foreground shadow-sm"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <TrendingUp className="h-3.5 w-3.5" />
            Weekly
          </button>
        </div>

        {/* Date selector */}
        <Input
          type="date"
          value={selectedDate}
          onChange={(e) => setSelectedDate(e.target.value)}
          className="w-auto"
        />
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      )}

      {/* Daily View */}
      {viewMode === "daily" && !isLoading && (
        <div className="space-y-4">
          {/* Summary cards */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
            {MEAL_TYPES.map((type) => (
              <Card key={type}>
                <CardContent className="p-4 text-center">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {MEAL_LABELS[type]}
                  </p>
                  <p className="mt-1 text-2xl font-bold text-foreground">
                    {dailyMealsByType[type] || 0}
                  </p>
                </CardContent>
              </Card>
            ))}
            <Card>
              <CardContent className="p-4 text-center">
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Total
                </p>
                <p className="mt-1 text-2xl font-bold text-primary">
                  {dailyTotal}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Detail table */}
          {(dailyData?.data?.length || 0) > 0 && (
            <div className="overflow-hidden rounded-lg border bg-card">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Meal Type
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Count
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Site
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {(dailyData?.data || []).map((meal) => (
                    <tr key={meal.id} className="hover:bg-muted/30 transition-colors">
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                            MEAL_COLORS[meal.mealType] || "bg-gray-100",
                          )}
                        >
                          {MEAL_LABELS[meal.mealType] || meal.mealType}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium text-foreground">
                        {meal.count}
                      </td>
                      <td className="px-4 py-3 text-sm text-muted-foreground">
                        {meal.siteId ? `Site ${meal.siteId}` : "All Sites"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {(dailyData?.data?.length || 0) === 0 && (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12 text-center">
                <UtensilsCrossed className="mb-4 h-12 w-12 text-muted-foreground/50" />
                <h3 className="text-lg font-medium text-foreground">
                  No meals recorded
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  No meal counts have been recorded for {selectedDate}.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Weekly Summary View */}
      {viewMode === "summary" && !isLoading && (
        <div className="space-y-4">
          {/* Week totals */}
          {summaryData?.data && (
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
              {summaryData.data.totals.map((t) => (
                <Card key={t.mealType}>
                  <CardContent className="p-4 text-center">
                    <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      {MEAL_LABELS[t.mealType] || t.mealType}
                    </p>
                    <p className="mt-1 text-2xl font-bold text-foreground">
                      {t.totalCount}
                    </p>
                  </CardContent>
                </Card>
              ))}
              <Card>
                <CardContent className="p-4 text-center">
                  <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Weekly Total
                  </p>
                  <p className="mt-1 text-2xl font-bold text-primary">
                    {summaryData.data.grandTotal}
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Weekly grid */}
          {weeklyGrid.length > 0 && (
            <div className="overflow-hidden rounded-lg border bg-card">
              <table className="w-full">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Day
                    </th>
                    {MEAL_TYPES.map((type) => (
                      <th
                        key={type}
                        className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground"
                      >
                        {MEAL_LABELS[type]}
                      </th>
                    ))}
                    <th className="px-4 py-3 text-center text-xs font-medium uppercase tracking-wider text-muted-foreground">
                      Total
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {weeklyGrid.map((day) => {
                    const dayTotal = Object.values(day.meals).reduce(
                      (s, c) => s + c,
                      0,
                    );
                    return (
                      <tr
                        key={day.date}
                        className={cn(
                          "hover:bg-muted/30 transition-colors",
                          day.date === today && "bg-primary/5",
                        )}
                      >
                        <td className="px-4 py-3 text-sm font-medium text-foreground">
                          {day.dayLabel}
                        </td>
                        {MEAL_TYPES.map((type) => (
                          <td
                            key={type}
                            className="px-4 py-3 text-center text-sm text-foreground"
                          >
                            {day.meals[type] || 0}
                          </td>
                        ))}
                        <td className="px-4 py-3 text-center text-sm font-semibold text-primary">
                          {dayTotal}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
